import React, { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Hands, HAND_CONNECTIONS } from '@mediapipe/hands';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import confetti from 'canvas-confetti';
import { Target, Trophy, RefreshCcw } from 'lucide-react';

const CHALLENGES = ['Hello', 'V', 'Love', 'Yes', 'No', 'Water', 'I', 'L'];

const cosineSim = (a, b) => {
  const dot  = a.reduce((s, v, i) => s + v * b[i], 0);
  const magA = Math.sqrt(a.reduce((s, v) => s + v * v, 0));
  const magB = Math.sqrt(b.reduce((s, v) => s + v * v, 0));
  return (magA && magB) ? dot / (magA * magB) : 0;
};

const getFingerStates = (lm) => {
  const tips = [4, 8, 12, 16, 20];
  const pips = [3, 6, 10, 14, 18];
  const horizExt = Math.abs(lm[4].x - lm[0].x) > Math.abs(lm[2].x - lm[0].x) * 1.1;
  const vertExt  = (lm[0].y - lm[4].y) > 0.08;
  const states   = [horizExt || vertExt ? 1 : 0];
  for (let i = 1; i < 5; i++) states.push(lm[tips[i]].y < lm[pips[i]].y ? 1 : 0);
  return states;
};

const GESTURES = [
  { name: 'Hello', f: [1, 1, 1, 1, 1] }, { name: 'V', f: [0, 1, 1, 0, 0] },
  { name: 'Love', f: [1, 1, 0, 0, 1] },  { name: 'Yes', f: [1, 0, 0, 0, 0] },
  { name: 'No', f: [0, 1, 1, 0, 0] },    { name: 'Water', f: [0, 1, 1, 1, 0] },
  { name: 'I', f: [0, 0, 0, 0, 1] },     { name: 'L', f: [1, 1, 0, 0, 0] }
];

const Practice = () => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const handsRef  = useRef(null);
  const rafRef    = useRef(null);

  const [targetSign, setTargetSign] = useState('Hello');
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  
  const targetSignRef = useRef(targetSign);
  const isSuccessRef = useRef(false);

  useEffect(() => { targetSignRef.current = targetSign; }, [targetSign]);

  const nextChallenge = () => {
    let next = CHALLENGES[Math.floor(Math.random() * CHALLENGES.length)];
    while (next === targetSignRef.current) next = CHALLENGES[Math.floor(Math.random() * CHALLENGES.length)];
    setTargetSign(next);
    setIsSuccess(false);
    isSuccessRef.current = false;
  };

  const onResults = useCallback((results) => {
    if (isSuccessRef.current) return; // Don't process if already succeeded

    const canvas = canvasRef.current;
    const video = webcamRef.current?.video;
    if (canvas && video) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (results.multiHandLandmarks) {
        for (const lm of results.multiHandLandmarks) {
          const m = lm.map(p => ({ ...p, x: 1 - p.x }));
          drawConnectors(ctx, m, HAND_CONNECTIONS, { color: 'rgba(255,255,255,0.8)', lineWidth: 3 });
          drawLandmarks(ctx, m, { color: 'var(--primary-color)', lineWidth: 2, radius: 5 });
        }
      }
    }

    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      const states = getFingerStates(results.multiHandLandmarks[0]);
      let best = null, bestScore = 0;
      
      for (const g of GESTURES) {
        const s = cosineSim(states, g.f);
        if (s > bestScore) { bestScore = s; best = g.name; }
      }

      if (best === targetSignRef.current && bestScore > 0.85) {
        isSuccessRef.current = true;
        setIsSuccess(true);
        setScore(s => s + 100);
        setStreak(s => s + 1);
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ['#659287', '#88BDA4', '#B1D3B9'] });
        
        setTimeout(() => {
          nextChallenge();
        }, 2000);
      }
    }
  }, []);

  useEffect(() => {
    const hands = new Hands({ locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4/${file}` });
    hands.setOptions({ maxNumHands: 1, modelComplexity: 1, minDetectionConfidence: 0.7, minTrackingConfidence: 0.6 });
    hands.onResults(onResults);
    handsRef.current = hands;
    hands.initialize().then(() => setModelReady(true));

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      hands.close();
    };
  }, [onResults]);

  useEffect(() => {
    if (!modelReady) return;
    let lastTime = 0;
    const loop = async (ts) => {
      if (ts - lastTime > 1000 / 12) {
        const video = webcamRef.current?.video;
        if (video && video.readyState === 4 && handsRef.current && !isSuccessRef.current) {
          try { await handsRef.current.send({ image: video }); } catch (_) {}
        }
        lastTime = ts;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [modelReady]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      <div className="glass-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', background: 'linear-gradient(135deg, var(--primary-color), var(--primary-hover))', color: 'white' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Target size={28} /> Sign Language Practice
          </h2>
          <p style={{ margin: '4px 0 0', opacity: 0.9 }}>Match the target sign to earn points and build your streak!</p>
        </div>
        
        <div style={{ display: 'flex', gap: '24px', textAlign: 'center' }}>
          <div>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.8, fontWeight: 600, letterSpacing: '1px' }}>SCORE</p>
            <h2 style={{ margin: 0, fontSize: '28px' }}>{score}</h2>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.8, fontWeight: 600, letterSpacing: '1px' }}>STREAK</p>
            <h2 style={{ margin: 0, fontSize: '28px', color: streak > 2 ? '#fbbf24' : 'white', display: 'flex', alignItems: 'center', gap: '4px' }}>
              {streak} <Trophy size={20} />
            </h2>
          </div>
        </div>
      </div>

      <div className="practice-grid">
        
        <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px' }}>
          <h3 style={{ color: 'var(--text-muted)', margin: '0 0 16px' }}>YOUR TARGET</h3>
          <h1 style={{ fontSize: '64px', margin: 0, color: 'var(--primary-color)' }}>{targetSign}</h1>
          {isSuccess && (
            <div style={{ marginTop: '24px', color: 'var(--success)', fontWeight: 600, fontSize: '20px', animation: 'pulse 1s infinite' }}>
              Perfect! Next sign loading...
            </div>
          )}
          <button className="btn" onClick={nextChallenge} style={{ marginTop: 'auto', width: '100%', justifyContent: 'center', background: 'rgba(0,0,0,0.05)' }}>
            <RefreshCcw size={16} /> Skip Sign
          </button>
        </div>

        <div className="video-wrapper" style={{ borderRadius: '16px', overflow: 'hidden', position: 'relative', minHeight: '400px', background: '#000', boxShadow: 'var(--glass-shadow)' }}>
          <Webcam ref={webcamRef} mirrored audio={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
          
          {!modelReady && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', color: 'white' }}>
              <h3>Loading AI Model...</h3>
            </div>
          )}
          
          {isSuccess && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(177, 211, 185, 0.4)', backdropFilter: 'blur(4px)' }}>
              <div style={{ background: 'white', padding: '20px 40px', borderRadius: '30px', color: 'var(--success)', fontSize: '32px', fontWeight: 700, boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                +100
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Practice;
